import type { PermissionEntry } from '@skewb-works/contracts';
import { Action, isAction, isSubject } from '@skewb-works/contracts';

/** Form values submitted when creating or updating a role. */
export interface RoleFormValues {
  name: string;
  description?: string;
  permissions: PermissionEntry[];
}

/** Every action granted on the `all` subject — the "full access" / admin shape. */
const ADMIN_PERMISSIONS: PermissionEntry[] = Object.values(Action).map((action) => ({
  action,
  subject: 'all' as const,
}));

/** Stable key for a single (action, subject) grant in the selection set. */
export function grantKey(action: string, subject: string): string {
  return `${action}::${subject}`;
}

/** A role is "admin" (full access) when it grants any action on the `all` subject. */
export function isAdminFromPermissions(permissions: PermissionEntry[]): boolean {
  return permissions.some((p) => p.subject === 'all');
}

/** Selection set of grant keys for the subject-scoped (non-admin) permissions. */
export function selectionFromPermissions(permissions: PermissionEntry[]): Set<string> {
  const set = new Set<string>();
  for (const grant of permissions) {
    if (grant.subject !== 'all') {
      set.add(grantKey(grant.action, grant.subject));
    }
  }
  return set;
}

/**
 * Resolve the permissions to submit. Admin short-circuits to all-actions-on-`all`;
 * otherwise each selected grant key is parsed back into a validated PermissionEntry,
 * silently dropping any key whose action or subject is no longer recognised.
 */
export function permissionsFromSelection(
  isAdmin: boolean,
  selected: Set<string>,
): PermissionEntry[] {
  if (isAdmin) return ADMIN_PERMISSIONS;
  const entries: PermissionEntry[] = [];
  for (const key of selected) {
    const [action, subject] = key.split('::');
    if (isAction(action) && isSubject(subject)) {
      entries.push({ action, subject });
    }
  }
  return entries;
}
